import { z } from 'zod';

const CHANNEL = 'dp-session';

const newTabId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

/** Every tab hears every message on the channel, its own included; this tells them apart. */
const THIS_TAB = newTabId();

const otherTabMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('signedIn'), userId: z.string().min(1), from: z.string().optional() }),
  z.object({ type: z.literal('signedOut'), from: z.string().optional() }),
]);
type Stamped = z.infer<typeof otherTabMessageSchema>;
export type OtherTabMessage = { readonly type: 'signedIn'; readonly userId: string } | { readonly type: 'signedOut' };

const openChannel = (): BroadcastChannel | null => {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
};

/** The app's other tabs share this tab's cookie, so a sign-in or sign-out here is theirs too. */
export const tellOtherTabs = (message: OtherTabMessage): void => {
  const channel = openChannel();
  if (channel === null) return;
  const stamped: Stamped = { ...message, from: THIS_TAB };
  channel.postMessage(stamped);
  channel.close();
};

export const onOtherTabs = (listener: (message: OtherTabMessage) => void): (() => void) => {
  const channel = openChannel();
  if (channel === null) return () => undefined;
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const message = otherTabMessageSchema.safeParse(event.data);
    if (!message.success || message.data.from === THIS_TAB) return;
    listener(message.data.type === 'signedIn' ? { type: 'signedIn', userId: message.data.userId } : { type: 'signedOut' });
  };
  return () => channel.close();
};
